/**
 * Google Tag Manager dataLayer helpers for the marketing site.
 * GTM container is loaded in index.html (GTM-KLKQFRQ9).
 */

type DataLayerEvent = Record<string, unknown> & { event: string };

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

/**
 * Push a custom event to the GTM dataLayer.
 */
export const pushGtmEvent = (payload: DataLayerEvent): void => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

/**
 * GA4 recommended `login` event. Partner tags in GTM listen for this custom event.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#login
 */
export const pushGtmLoginEvent = (method: string): void => {
  pushGtmEvent({
    event: 'login',
    method,
  });
};

type ClerkLikeUser = {
  externalAccounts?: Array<{ provider?: string | null; verification?: { status?: string | null } | null }>;
  phoneNumbers?: unknown[];
  emailAddresses?: unknown[];
} | null | undefined;

/**
 * Best-effort auth method for GA4 `method` (google, email, discord, …).
 */
export const resolveLoginMethod = (user: ClerkLikeUser): string => {
  if (!user) return 'unknown';

  const verifiedOauth =
    user.externalAccounts?.find((account) => account.verification?.status === 'verified') ??
    user.externalAccounts?.[0];

  if (verifiedOauth?.provider) {
    return verifiedOauth.provider.replace(/^oauth_/, '');
  }

  if (user.phoneNumbers && user.phoneNumbers.length > 0) return 'phone';
  if (user.emailAddresses && user.emailAddresses.length > 0) return 'email';

  return 'clerk';
};
